import React, { useEffect, useMemo, useState } from 'react';
import { MdLocalHospital } from 'react-icons/md';
import { ipApi } from '../../services/api';
import { Q } from '../../config/queueStatus';

/**
 * Fullscreen waiting-room TV display.
 * Open at /display/waiting-room while already signed in on this browser,
 * or after reception opens it from a logged-in session.
 * Do NOT pass staff tokens in the URL (security).
 */
function statusColor(s) {
  if (s === Q.WAITING_DOCTOR) return '#10b981';
  if (s === Q.IN_CONSULTATION) return '#8b5cf6';
  if (s === Q.CHECKED_IN || s === Q.AWAITING_PAYMENT) return '#f59e0b';
  return '#94a3b8';
}

function statusLabel(s) {
  if (s === Q.WAITING_DOCTOR) return 'Waiting';
  if (s === Q.IN_CONSULTATION) return 'Now consulting';
  if (s === Q.CHECKED_IN) return 'Checked-in';
  if (s === Q.AWAITING_PAYMENT) return 'At billing';
  return s || '';
}

export default function QueueTvDisplay() {
  const [queue, setQueue] = useState([]);
  const [hospitalName, setHospitalName] = useState('');
  const [clock, setClock] = useState(new Date());
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const hospitalId = params.get('hospitalId') || '';

  useEffect(() => {
    // Security: never accept staff tokens from URL (history, screenshots, kiosk leak).
    // Strip token from address bar if someone still uses an old link.
    if (params.get('token')) {
      const clean = new URL(window.location.href);
      clean.searchParams.delete('token');
      window.history.replaceState({}, '', clean.pathname + clean.search);
    }
  }, [params]);

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await ipApi.getQueue({ limit: 150 });
        if (cancelled) return;
        const rows = (data?.queue || []).filter((q) => {
          const d = (q.visitDate || (q.createdAt || '').slice(0, 10));
          const today = new Date().toISOString().slice(0, 10);
          return !d || d === today;
        });
        setQueue(rows);
        setError('');
        if (data?.hospitalName) setHospitalName(data.hospitalName);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load queue');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const poll = setInterval(load, 8000);
    return () => { cancelled = true; clearInterval(poll); };
  }, [hospitalId]);

  useEffect(() => {
    // Hospital branding for TV screen
    ipApi.getHospitalProfile().then((p) => {
      if (p?.hospital?.name) setHospitalName(p.hospital.name);
    }).catch(() => {});
  }, []);

  const byDoctor = useMemo(() => {
    const map = {};
    queue
      .filter((q) => q.status !== Q.COMPLETED && q.status !== 'Deleted' && q.status !== 'Cancelled')
      .forEach((q) => {
        const doc = q.doctor || 'General';
        if (!map[doc]) map[doc] = [];
        map[doc].push(q);
      });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  }, [queue]);

  const nowCalling = useMemo(
    () => queue.filter((q) => q.status === Q.IN_CONSULTATION),
    [queue]
  );

  return (
    <div className="tv-display">
      <header className="tv-header">
        <div className="tv-brand">
          <MdLocalHospital className="tv-logo" />
          <div>
            <div className="tv-hospital">{hospitalName}</div>
            <div className="tv-sub">OPD Waiting Room · Live Queue</div>
          </div>
        </div>
        <div className="tv-clock">
          <div className="tv-time">{clock.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</div>
          <div className="tv-date">{clock.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</div>
        </div>
        <span className="tv-live"><span className="tv-live-dot" /> LIVE</span>
      </header>

      {error && <div className="tv-error">{error} — open this screen while logged in, or pass a valid session.</div>}
      {loading && !queue.length && <div className="tv-loading">Loading queue…</div>}

      {nowCalling.length > 0 && (
        <div className="tv-now-strip">
          {nowCalling.map((p) => (
            <div key={p.queueId || p.token} className="tv-now-card">
              <div className="tv-now-label">Now calling</div>
              <div className="tv-now-token">Token {p.token || '—'}</div>
              <div className="tv-now-name">{p.patientName || p.name}</div>
              <div className="tv-now-doc">{p.doctor}</div>
            </div>
          ))}
        </div>
      )}

      <div className="tv-grid">
        {byDoctor.length === 0 && !loading && (
          <div className="tv-empty">No patients waiting — queue is clear</div>
        )}
        {byDoctor.map(([doctor, pts]) => {
          const consulting = pts.find((p) => p.status === Q.IN_CONSULTATION);
          const waiting = pts.filter((p) => p.status !== Q.IN_CONSULTATION);
          return (
            <div key={doctor} className="tv-col">
              <div className="tv-col-head">
                <span className="tv-avatar">{(doctor || 'D').charAt(0)}</span>
                <div>
                  <div className="tv-doc-name">{doctor}</div>
                  <div className="tv-doc-count">{pts.length} in queue</div>
                </div>
              </div>
              {consulting && (
                <div className="tv-consulting">
                  <div className="tv-consulting-label">In consultation</div>
                  <div className="tv-consulting-token">#{consulting.token || '—'}</div>
                  <div className="tv-consulting-name">{consulting.patientName || consulting.name}</div>
                </div>
              )}
              <div className="tv-list">
                {waiting.slice(0, 12).map((p, i) => (
                  <div key={p.queueId || i} className="tv-row">
                    <span className="tv-token">{p.token || String(i + 1).padStart(3, '0')}</span>
                    <span className="tv-pname">{p.patientName || p.name}</span>
                    <span className="tv-status" style={{ background: statusColor(p.status) }} title={statusLabel(p.status)} />
                  </div>
                ))}
                {waiting.length === 0 && <div className="tv-col-empty">No one waiting</div>}
                {waiting.length > 12 && <div className="tv-more">+{waiting.length - 12} more</div>}
              </div>
            </div>
          );
        })}
      </div>

      <footer className="tv-footer">
        Please wait for your token number to be called · Keep social distancing · Powered by HCEN
      </footer>
    </div>
  );
}
